import sortBy from 'lodash/sortBy';

import { ItemsIndexKeys } from 'components/ToggleListModal/types';
import {
  WIDGET_FILTER_SELECTED_ENDPOINT,
  WIDGET_FILTER_VALUES_ENDPOINT,
} from 'constants/apis';
import {
  EARNED_SEARCHES_FILTERS_OPERATORS,
  FILTER_IDS,
} from 'constants/constants';
import { TransformedFilter } from 'pages/EarnedSearch/state/types/filters';
import { addPageMessageWithDefaultTimeout } from 'reducers/page-messages';
import { performGet } from 'services/rest-service/rest-service';

import {
  setFilters,
  setFilterValues,
  setLanguageV2Filters,
  setLocationV2Filters,
  setModalError,
} from '../state/actionCreators/filtersActionCreators';
import { setSearchDataLoading } from '../state/actionCreators/searchBuilderActionCreators';
import {
  LANGUAGE_KEY,
  LANGUAGE_KEY_V2,
  LOCATION_KEY,
  LOCATION_KEY_V2,
} from '../state/reducers/filtersReducer';

const FILTER_V2_IDS_MAPPER = {
  [LANGUAGE_KEY_V2]: FILTER_IDS.CURATED_LANGUAGE_FILTER,
  [LOCATION_KEY_V2]: FILTER_IDS.CURATED_LOCATION_FILTER,
};
interface StoredFilterValues {
  key: string;
  value: string;
}

export const transformDataForModal = (
  filterValues: StoredFilterValues[],
): TransformedFilter[] =>
  sortBy(
    filterValues.map(filter => ({
      id: filter.value,
      name: filter.key,
      value: filter.key,
    })),
    'value',
  );

export const convertFiltersForPreviewSearch = (
  selectedFilters: TransformedFilter[],
  allFilters: TransformedFilter[],
) =>
  selectedFilters.map(({ value }) => {
    // check if the property filterValue is full of digits
    if (/^\d+$/.test(value)) {
      return allFilters.find(
        (storedFilter: TransformedFilter) => storedFilter.id === value,
      )?.value;
    }
    // if its a string but contains characters different than digits then do nothing and return the same property
    return value;
  });

export const convertFilters = filters => {
  const filtersData = {};
  filters.forEach(filter => {
    filtersData[`${filter.name.toLowerCase()}V2`] = {
      operator:
        filter.filterType === EARNED_SEARCHES_FILTERS_OPERATORS.must
          ? EARNED_SEARCHES_FILTERS_OPERATORS.is
          : EARNED_SEARCHES_FILTERS_OPERATORS.not,
      values: filter.selected,
    };
  });
  return filtersData;
};

export const getFiltersById = (filterId: string) => async dispatch => {
  const url = WIDGET_FILTER_VALUES_ENDPOINT.replace('{filterId}', filterId);
  try {
    const response = await performGet(url);
    const filters = transformDataForModal(response?.data);
    dispatch(setFilterValues(filterId, filters));
    return filters;
  } catch (error) {
    dispatch(setModalError(error));
  }
};

const dispatchFiltersV2 = (
  dispatch,
  filterV1Converted: Record<string, any> = {},
) => {
  if (filterV1Converted[LOCATION_KEY_V2]) {
    dispatch(setLocationV2Filters({ ...filterV1Converted[LOCATION_KEY_V2] }));
  }
  if (filterV1Converted[LANGUAGE_KEY_V2]) {
    dispatch(setLanguageV2Filters({ ...filterV1Converted[LANGUAGE_KEY_V2] }));
  }
};

const iterateOverFiltersV1 = (
  persistedFiltersV1,
  allFilterLocationsV2,
  allFiltersLanguagesV2,
): Record<string, any> => {
  const filterV1ConvertedTemp: Record<string, any> = {};
  Object.keys(persistedFiltersV1).forEach(persistedFilterKey => {
    const { operator, values } = persistedFiltersV1[persistedFilterKey];
    const sourceOfData =
      persistedFilterKey === LOCATION_KEY
        ? allFilterLocationsV2
        : allFiltersLanguagesV2;
    filterV1ConvertedTemp[
      persistedFilterKey === LOCATION_KEY ? LOCATION_KEY_V2 : LANGUAGE_KEY_V2
    ] = {
      operator: '',
      values: [],
    };
    filterV1ConvertedTemp[persistedFilterKey + 'V2'].operator = operator;
    filterV1ConvertedTemp[
      persistedFilterKey + 'V2'
    ].values = sourceOfData.filter(languageFilter =>
      values.includes(languageFilter.name),
    );
  });
  return filterV1ConvertedTemp;
};

export const getFilterObject = (
  id: string,
  type: string,
  selected: string[],
) => {
  return {
    filterId: id,
    filterType: type,
    selected: selected,
  };
};

export const getFilterObjectUsingV1Operator = (
  id: string,
  operator: string,
  selected: string[],
) => {
  const filterType =
    operator === EARNED_SEARCHES_FILTERS_OPERATORS.is
      ? EARNED_SEARCHES_FILTERS_OPERATORS.must
      : EARNED_SEARCHES_FILTERS_OPERATORS.must_not;
  return getFilterObject(id, filterType, selected);
};

export const getFiltersObjectsList = (filtersObject: Record<string, any>) =>
  Object.entries(filtersObject || {}).reduce((acc, [key, data]) => {
    const id = FILTER_V2_IDS_MAPPER[key];
    if (!id) {
      return acc;
    }
    return [
      ...acc,
      getFilterObjectUsingV1Operator(
        id,
        data.operator,
        data.values.map(v => v.id),
      ),
    ];
  }, []);

export const getFiltersRequestObject = (filtersObject: Record<string, any>) => {
  const allowedFiltersToUse = [LANGUAGE_KEY_V2, LOCATION_KEY_V2];
  const filters = Object.keys(filtersObject)
    .filter(filter => allowedFiltersToUse.includes(filter))
    .reduce((acc: any, filterKey) => {
      if (filtersObject[filterKey].values.length > 0) {
        const filterValues = filtersObject[filterKey].values.map(
          (filterValueData: any) => {
            if (typeof filterValueData === 'object') {
              if (filterValueData.hasOwnProperty(ItemsIndexKeys.ID)) {
                return filterValueData.id;
              }
              if (filterValueData.hasOwnProperty(ItemsIndexKeys.VALUE)) {
                return filterValueData.value;
              }
            }
          },
        );
        const filter = getFilterObjectUsingV1Operator(
          FILTER_V2_IDS_MAPPER[filterKey],
          filtersObject[filterKey].operator,
          filterValues,
        );
        return [...acc, filter];
      }
      return acc;
    }, []);

  return {
    persistedFilterRequest: {
      filters,
    },
  };
};

export const getPersistedFiltersV2BySearchId = (
  searchId: string,
  shouldStartPreviewProcess = false,
  errorMessage,
  persistedFiltersV1,
  allFilterLocationsV2,
  allFiltersLanguagesV2,
  onLoad = data => null,
) => async dispatch => {
  const url = WIDGET_FILTER_SELECTED_ENDPOINT.replace('{searchId}', searchId);
  try {
    if (persistedFiltersV1[LANGUAGE_KEY] || persistedFiltersV1[LOCATION_KEY]) {
      const filterV1Converted: Record<string, any> = iterateOverFiltersV1(
        persistedFiltersV1,
        allFilterLocationsV2,
        allFiltersLanguagesV2,
      );
      onLoad({
        filtersV2: getFiltersObjectsList(filterV1Converted),
      });
      dispatchFiltersV2(dispatch, filterV1Converted);
      if (shouldStartPreviewProcess) {
        dispatch(setSearchDataLoading(false));
      }
      return;
    }

    const filters = await performGet(url, { context: `SEARCH:${searchId}` });
    dispatch(setFilters(convertFilters(filters?.data)));
    if (shouldStartPreviewProcess) {
      dispatch(setSearchDataLoading(false));
    }
  } catch (error) {
    dispatch(
      addPageMessageWithDefaultTimeout({
        text: errorMessage,
        status: 'danger',
      }),
    );
  }
};
